/** vue-router's NavigationFailureType.duplicated — the benign, high-frequency case. */
const DUPLICATED = 16;

/**
 * Builds the `onSwallowed` trace below. Takes the Bugsnag client rather than importing it, so a
 * bundle that doesn't run Bugsnag isn't made to carry it for a breadcrumb that could never fire.
 *
 * @param {{ isStarted: () => boolean, leaveBreadcrumb: Function }} [bugsnag]
 * @returns {(failure: { type: number, to?: { name?: string, path?: string } }) => void}
 */
export function traceSwallowedFailure(bugsnag) {
  return (failure) => {
    // Path-only route records resolve with no `name`, which would breadcrumb the destination away.
    const destination = failure.to?.name || failure.to?.path || 'unknown';
    if (process.env.NODE_ENV === 'development') {
      console.debug('[router] swallowed navigation failure', failure.type, destination);
    }
    // Breadcrumbs cap at 25, so breadcrumbing the duplicate-nav case would evict the navigation
    // trail from unrelated crash reports — it is the case this patch exists to make routine.
    if (failure.type !== DUPLICATED && bugsnag && bugsnag.isStarted()) {
      bugsnag.leaveBreadcrumb('Navigation failure swallowed', { type: failure.type, to: destination });
    }
  };
}

/**
 * Patches router push/replace so an expected navigation failure resolves with the failure instead
 * of rejecting; real guard errors still reject, and `onSwallowed` is a swallowed failure's only trace.
 *
 * @param {import('vue-router').default} router - the router instance to patch, in place.
 * @param {(error: unknown) => boolean} isNavigationFailure - `vueRouter.isNavigationFailure`,
 *   passed in to avoid importing vue-router here.
 * @param {(failure: unknown) => void} [onSwallowed] - called with the failure before it resolves.
 */
export function patchRouterNavigationFailures(router, isNavigationFailure, onSwallowed = () => {}) {
  ['push', 'replace'].forEach((method) => {
    const original = router[method].bind(router);
    router[method] = (location, onComplete, onAbort) => {
      const result = original(location, onComplete, onAbort);
      return result && result.catch
        ? result.catch((error) => {
            if (!isNavigationFailure(error)) throw error;
            onSwallowed(error);
            return error;
          })
        : result;
    };
  });
}
